import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface FailureAgentScore {
  agent_name: string
  score: number
  pass_count: number
  fail_count: number
  dsq_count: number
  disagree_count: number
  avg_time: number | null
}

export interface AgentTotal {
  agent_name: string
  total_score: number
  failure_count: number
  pass_count: number
  fail_count: number
  dsq_count: number
  disagree_count: number
}

export interface Failure {
  id: string
  title: string
  tag_name: string
  tag_names: string[]
  github_repository: string
  branch: string
  failing_changeset: string
  fixed_changeset: string
  test_file: string
  clean_branch: string
  clean_status: string
  clean_error: string
  refined_branch: string
  refined_status: string
  refined_error: string
  short_id: string
  agent_scores: FailureAgentScore[]
  winner_agent: string | null
  last_run_date: string | null
  created_at: string
}

export interface FailureAgent {
  id: string
  name: string
}

export interface FailureTag {
  name: string
  count: number
}

interface FailuresState {
  items: Failure[]
  selectionItems: MinimalFailure[]
  tags: FailureTag[]
  agents: FailureAgent[]
  agentTotals: AgentTotal[]
  loading: boolean
  error: string | null
  total: number
  uncleanedCount: number
  cleaningCount: number
  unrefinedCount: number
  refiningCount: number
  page: number
  pageSize: number
  hasMore: boolean
}

const initialState: FailuresState = {
  items: [],
  selectionItems: [],
  tags: [],
  agents: [],
  agentTotals: [],
  loading: false,
  error: null,
  total: 0,
  uncleanedCount: 0,
  cleaningCount: 0,
  unrefinedCount: 0,
  refiningCount: 0,
  page: 0,
  pageSize: 50,
  hasMore: true,
}

export const fetchFailures = createAsyncThunk('failures/fetchFailures', async (filters?: { tag?: string | null; search?: string | null } | string | null) => {
  const params = new URLSearchParams({ page: '1', pageSize: '50' })
  // Support both old string form (tag only) and new object form
  if (typeof filters === 'string') {
    params.set('tag', filters)
  } else if (filters) {
    if (filters.tag) params.set('tag', filters.tag)
    if (filters.search) params.set('search', filters.search)
  }
  const response = await fetch(`/api/failures?${params.toString()}`)
  if (!response.ok) throw new Error('Failed to fetch failures')
  return response.json() as Promise<{ failures: Failure[]; tags: FailureTag[]; agents: FailureAgent[]; agent_totals: AgentTotal[]; total: number; page: number; pageSize: number; uncleaned_count: number; cleaning_count: number; unrefined_count: number; refining_count: number }>
})

export interface MinimalFailure {
  id: string
  title: string
  tag_name: string
  tag_names: string[]
  short_id: string
}

export const fetchAllFailuresForSelection = createAsyncThunk('failures/fetchAllFailuresForSelection', async () => {
  const response = await fetch('/api/failures?select=minimal')
  if (!response.ok) throw new Error('Failed to fetch failures')
  return response.json() as Promise<{ failures: MinimalFailure[]; tags: FailureTag[]; total: number }>
})

export const fetchMoreFailures = createAsyncThunk('failures/fetchMoreFailures', async ({ page, tag, search }: { page: number; tag?: string | null; search?: string | null }) => {
  const params = new URLSearchParams({ page: String(page), pageSize: '50' })
  if (tag) params.set('tag', tag)
  if (search) params.set('search', search)
  const response = await fetch(`/api/failures?${params.toString()}`)
  if (!response.ok) throw new Error('Failed to fetch failures')
  return response.json() as Promise<{ failures: Failure[]; tags: FailureTag[]; agents: FailureAgent[]; agent_totals: AgentTotal[]; total: number; page: number; pageSize: number; uncleaned_count: number; cleaning_count: number; unrefined_count: number; refining_count: number }>
})

export const createFailure = createAsyncThunk(
  'failures/createFailure',
  async (failure: {
    title: string
    tag_name: string
    github_repository: string
    branch: string
    failing_changeset: string
    fixed_changeset: string
    test_file: string
  }) => {
    const response = await authedFetch('/api/failures', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(failure),
    })
    if (!response.ok) throw new Error('Failed to create failure')
    return response.json() as Promise<Failure>
  }
)

export const updateFailureShortId = createAsyncThunk(
  'failures/updateFailureShortId',
  async ({ id, short_id }: { id: string; short_id: string }) => {
    const response = await authedFetch(`/api/failures/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ short_id }),
    })
    if (!response.ok) throw new Error('Failed to update short_id')
    return { id, short_id }
  }
)

export const deleteFailure = createAsyncThunk(
  'failures/deleteFailure',
  async (id: string) => {
    const response = await authedFetch(`/api/failures/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to delete failure')
    return id
  }
)

const failuresSlice = createSlice({
  name: 'failures',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFailures.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchFailures.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.failures
        state.tags = action.payload.tags
        state.agents = action.payload.agents || []
        state.agentTotals = action.payload.agent_totals || []
        state.total = action.payload.total
        state.uncleanedCount = action.payload.uncleaned_count ?? 0
        state.cleaningCount = action.payload.cleaning_count ?? 0
        state.unrefinedCount = action.payload.unrefined_count ?? 0
        state.refiningCount = action.payload.refining_count ?? 0
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(fetchFailures.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch failures'
      })
      .addCase(fetchAllFailuresForSelection.fulfilled, (state, action) => {
        state.selectionItems = action.payload.failures
        state.tags = action.payload.tags
      })
      .addCase(fetchMoreFailures.fulfilled, (state, action) => {
        state.items = [...state.items, ...action.payload.failures]
        state.tags = action.payload.tags
        state.agents = action.payload.agents || []
        state.agentTotals = action.payload.agent_totals || []
        state.total = action.payload.total
        state.uncleanedCount = action.payload.uncleaned_count ?? 0
        state.cleaningCount = action.payload.cleaning_count ?? 0
        state.unrefinedCount = action.payload.unrefined_count ?? 0
        state.refiningCount = action.payload.refining_count ?? 0
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.hasMore = action.payload.page * action.payload.pageSize < action.payload.total
      })
      .addCase(createFailure.fulfilled, (state, action) => {
        state.items.unshift(action.payload)
        state.total += 1
      })
      .addCase(updateFailureShortId.fulfilled, (state, action) => {
        const failure = state.items.find((f) => f.id === action.payload.id)
        if (failure) failure.short_id = action.payload.short_id
      })
      .addCase(deleteFailure.fulfilled, (state, action) => {
        state.items = state.items.filter((f) => f.id !== action.payload)
        state.total -= 1
      })
      .addCase(deleteFailure.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to delete failure'
      })
  },
})

export default failuresSlice.reducer
